"use client";

import { formatTime } from "@/lib/utils";
import { isActivity } from "@/lib/types";
import { Link } from "@/i18n/navigation";
import type { ScheduleData, ScheduleSlot } from "./use-schedule-data";
import { ScheduleBlock } from "./schedule-block";
import { ScheduleNowLine } from "./schedule-now-line";
import { useScheduleBuilder } from "./schedule-context";

/** Minimum block height in pixels — ensures short sets are readable */
const MIN_BLOCK_PX = 60;
const STAGE_MIN_WIDTH = 140;
const STAGE_MAX_WIDTH = 250;

function TimeAxis({
  dayStartMinutes,
  dayEndMinutes,
  pixelsPerMinute,
}: {
  dayStartMinutes: number;
  dayEndMinutes: number;
  pixelsPerMinute: number;
}) {
  const markers: { minutes: number; label: string; isHour: boolean }[] = [];
  for (let m = dayStartMinutes; m <= dayEndMinutes; m += 30) {
    const h = Math.floor(m / 60) % 24;
    const mm = m % 60;
    const timeStr = `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    markers.push({
      minutes: m,
      label: formatTime(timeStr) || timeStr,
      isHour: mm === 0,
    });
  }

  return (
    <>
      {markers.map((marker) => {
        const top = (marker.minutes - dayStartMinutes) * pixelsPerMinute;
        return (
          <div
            key={marker.minutes}
            className="absolute left-0 right-0 pointer-events-none"
            style={{ top: `${top}px` }}
          >
            <div className="flex items-start">
              {/* Time label */}
              <span
                className={`w-14 shrink-0 -mt-2 pr-2 text-right font-mono text-[10px] leading-none ${
                  marker.isHour ? "text-neutral-500" : "text-neutral-700"
                }`}
              >
                {marker.label}
              </span>
              {/* Gridline */}
              <div
                className={`flex-1 border-t ${
                  marker.isHour
                    ? "border-neutral-800/60"
                    : "border-neutral-800/30"
                }`}
              />
            </div>
          </div>
        );
      })}
    </>
  );
}

/** Standalone time gutter — rendered outside the scroll container so it never scrolls horizontally. */
export function TimeGutterOverlay({
  dayStartMinutes,
  dayEndMinutes,
  pixelsPerMinute,
  gridHeight,
}: {
  dayStartMinutes: number;
  dayEndMinutes: number;
  pixelsPerMinute: number;
  gridHeight: number;
}) {
  const markers: { minutes: number; label: string; isHour: boolean }[] = [];
  for (let m = dayStartMinutes; m <= dayEndMinutes; m += 30) {
    const h = Math.floor(m / 60) % 24;
    const mm = m % 60;
    const timeStr = `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    markers.push({
      minutes: m,
      label: formatTime(timeStr) || timeStr,
      isHour: mm === 0,
    });
  }

  return (
    <div
      className="absolute left-0 top-0 z-10 w-14 pointer-events-none"
      style={{ height: `${gridHeight}px` }}
    >
      {markers.map((marker) => {
        const top = (marker.minutes - dayStartMinutes) * pixelsPerMinute;
        return (
          <span
            key={marker.minutes}
            className={`absolute right-0 -mt-2 pr-2 text-right font-mono text-[10px] leading-none bg-dark px-1 ${
              marker.isHour ? "text-neutral-500" : "text-neutral-700"
            }`}
            style={{ top: `${top}px` }}
          >
            {marker.label}
          </span>
        );
      })}
    </div>
  );
}

export function StageHeaders({
  stages,
  stageVenues,
}: {
  stages: string[];
  stageVenues?: Record<string, { slug: string; citySlug: string }>;
}) {
  return (
    <div className="flex border-b border-neutral-800">
      {/* Gutter for time axis — sticky when scrolling horizontally */}
      <div className="sticky left-0 z-10 w-14 shrink-0 bg-dark" />
      {/* Stage columns */}
      {stages.map((stage) => {
        const venue = stageVenues?.[stage];
        const label = (
          <span className="font-mono text-[10px] font-bold tracking-widest leading-tight line-clamp-2">
            {stage.toUpperCase()}
          </span>
        );
        return (
          <div
            key={stage}
            className="flex-1 px-1 py-2 text-center"
            style={{ minWidth: `${STAGE_MIN_WIDTH}px`, maxWidth: `${STAGE_MAX_WIDTH}px` }}
          >
            {venue ? (
              <Link
                href={`/${venue.citySlug}/${venue.slug}`}
                className="text-neutral-400 hover:text-white transition-colors"
              >
                {label}
              </Link>
            ) : (
              <span className="text-neutral-500">{label}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ScheduleGrid({
  data,
  visibleStages,
  festivalDate,
  blockedSpotifyIds,
  festivalId,
  eventId,
  showOnlyMySchedule,
}: {
  data: ScheduleData;
  visibleStages: string[];
  festivalDate: string;
  blockedSpotifyIds: string[];
  festivalId: number;
  eventId: number;
  showOnlyMySchedule: boolean;
}) {
  const { savedSetKeys } = useScheduleBuilder();

  // Dynamic scale: ensure shortest block gets at least MIN_BLOCK_PX height
  const minDuration = data.slots.length > 0
    ? Math.min(...data.slots.map((s) => s.durationMinutes))
    : 30;
  const pixelsPerMinute = Math.min(5, Math.max(2, Math.ceil(MIN_BLOCK_PX / minDuration)));

  const totalMinutes = data.dayEndMinutes - data.dayStartMinutes;
  const gridHeight = totalMinutes * pixelsPerMinute;

  // Map visible stages to their index in the full stages array
  const stageToVisibleIndex = new Map(
    visibleStages.map((s, i) => [s, i])
  );

  // Filter slots to visible stages
  let visibleSlots = data.slots.filter((slot) =>
    stageToVisibleIndex.has(data.stages[slot.stageIndex])
  );

  // Apply "my schedule" filter
  if (showOnlyMySchedule) {
    visibleSlots = visibleSlots.filter((slot) =>
      slot.attractions.some((a) => !isActivity(a) && savedSetKeys.has(`${eventId}-${a.id}`))
    );
  }

  // When filtering to my schedule, only show stages that have saved sets
  const activeStages = showOnlyMySchedule
    ? visibleStages.filter((stage) =>
        visibleSlots.some((slot) => data.stages[slot.stageIndex] === stage)
      )
    : visibleStages;

  // Remap stage indexes for the active set
  const activeStageIndex = new Map(
    activeStages.map((s, i) => [s, i])
  );

  // Group slots by active stage index
  const slotsByStage = new Map<number, ScheduleSlot[]>();
  for (const slot of visibleSlots) {
    const stageName = data.stages[slot.stageIndex];
    const idx = activeStageIndex.get(stageName);
    if (idx == null) continue;
    const list = slotsByStage.get(idx) || [];
    list.push(slot);
    slotsByStage.set(idx, list);
  }

  return (
    <div>
      {/* Grid body */}
      <div className="relative" style={{ height: `${gridHeight}px` }}>
        {/* Time axis + gridlines */}
        <TimeAxis
          dayStartMinutes={data.dayStartMinutes}
          dayEndMinutes={data.dayEndMinutes}
          pixelsPerMinute={pixelsPerMinute}
        />

        {/* Now line */}
        <ScheduleNowLine
          dayStartMinutes={data.dayStartMinutes}
          festivalDate={festivalDate}
          pixelsPerMinute={pixelsPerMinute}
        />

        {/* Stage columns with positioned blocks */}
        <div className="absolute left-14 right-0 top-0 bottom-0 flex">
          {activeStages.map((stage, visIdx) => {
            const stageSlots = slotsByStage.get(visIdx) || [];
            return (
              <div
                key={stage}
                className="relative flex-1 border-l border-neutral-800/20"
                style={{ minWidth: `${STAGE_MIN_WIDTH}px`, maxWidth: `${STAGE_MAX_WIDTH}px` }}
              >
                {stageSlots.map((slot) => (
                  <ScheduleBlock
                    key={isActivity(slot.attraction) ? `act-${slot.attraction.activity_name}-${slot.startMinutes}` : slot.attraction.id}
                    slot={slot}
                    dayStartMinutes={data.dayStartMinutes}
                    blockedSpotifyIds={blockedSpotifyIds}
                    festivalId={festivalId}
                    eventId={eventId}
                    pixelsPerMinute={pixelsPerMinute}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
